/**
 * VOLUME TOUCHED = "I WANT TO HEAR THIS."
 *
 * The owner's rule (2026-08-17): while a trailer is playing, the user reaching
 * for their volume — the remote's volume buttons on a television, the rocker
 * on a phone, the volume keys on a laptop — means unmute THE TRAILER THAT IS
 * PLAYING. Only that one: the next trailer starts muted as always. It matters
 * most on TV, where there is otherwise no comfortable way to unmute at all.
 *
 * This module is the meeting point, because the signal and the listener live
 * in different worlds:
 *
 *   • The ANDROID/FIRE TV SHELL owns the volume keys (the page never sees
 *     KEYCODE_VOLUME_UP). It forwards a press by dispatching
 *     `new CustomEvent('jt:volumekey')` on `window` (or calling
 *     `window.__jtVolumeKey?.()`, which does the same) from its JS bridge.
 *   • BROWSERS mostly hide hardware volume from pages, but where a platform
 *     does deliver it as a key ('AudioVolumeUp'/'AudioVolumeDown' — some
 *     desktop browsers do), the listener below catches it. No promises are
 *     made where the platform tells us nothing; that honesty is the contract.
 *   • THE SURFACE that currently holds the trailer claim (the hero, a tile)
 *     subscribes with `onVolumeIntent` and, if its trailer is actually
 *     showing, unmutes its own YtHandle. The subscriber decides relevance —
 *     this module only says "the volume was touched".
 *
 * Never store "unmuted" anywhere durable: the intent is for the trailer on
 * screen at that moment, and it dies with it.
 */

type Off = () => void;

const listeners = new Set<() => void>();
let wired = false;

function fire(): void {
  for (const fn of [...listeners]) fn();
}

function wire(): void {
  if (wired || typeof window === 'undefined') return;
  wired = true;

  // The shell's bridge: a CustomEvent, and a bare function for the simplest
  // possible evaluateJavascript call from Kotlin.
  window.addEventListener('jt:volumekey', fire);
  (window as Window & { __jtVolumeKey?: () => void }).__jtVolumeKey = fire;

  // The rare browsers that hand volume keys to the page as keys.
  window.addEventListener('keydown', (e) => {
    if (e.key === 'AudioVolumeUp' || e.key === 'AudioVolumeDown') fire();
  });
}

/** Hear about the next volume touches. Returns the unsubscribe. */
export function onVolumeIntent(fn: () => void): Off {
  wire();
  listeners.add(fn);
  return () => listeners.delete(fn);
}
